import { apiFetch } from '@/lib/api-client';
import { components } from '@/types/ff.v1';

/**
 * Re-exported for convenience
 */
type SendFriendConnectionViewType =
  components['schemas']['SendFriendConnectionView'];

type FriendshipView = components['schemas']['FriendshipView'];

/**
 * Shape returned by the public invitation-lookup endpoint.
 */
interface FriendInvitationLookup {
  email?: string;
  inviterName?: string;
}

/**
 * Resolve an invitation token to the invited email + inviter name so the
 * signup form can be pre-filled. Public endpoint — no auth required.
 * Returns null when the token is unknown or expired (404).
 */
export const getInvitationByToken = async (
  token: string
): Promise<FriendInvitationLookup | null> => {
  const response = await apiFetch(
    `/api/v1/friends/invitations/by-token?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }
  );

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error('Failed to look up invitation');
  }
  return response.json();
};

export class SelfInvitationError extends Error {
  constructor() {
    super('You cannot accept an invitation you sent to yourself.');
    this.name = 'SelfInvitationError';
  }
}

/**
 * Accept a pending invitation as the signed-in user (used after an existing
 * account signs in via an invite link). Returns null when the invite has
 * already been used or expired (404) so callers can safely ignore it.
 * Throws SelfInvitationError when the authenticated user is the invitation's creator.
 */
export const acceptInvitationByToken = async (
  token: string
): Promise<FriendshipView | null> => {
  const response = await apiFetch('/api/v1/friends/invitations/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token })
  });

  if (response.status === 404) {
    return null;
  }
  if (response.status === 400) {
    const body = await response.json().catch(() => ({}));
    const code =
      body.errorCode ?? body.code ?? body.error ?? body.description ?? '';
    if (code === 'INVALID_FRIEND_SELF_INVITATION') {
      throw new SelfInvitationError();
    }
  }
  if (!response.ok) {
    throw new Error('Failed to accept invitation');
  }
  return response.json();
};

/**
 * Accept a friend request as the signed-in recipient (the existing-user flow
 * landed on from `/friends/accept?requestId=…`). `friendshipId` is the request
 * id from the URL. Throws on any non-2xx so the page can show an error state.
 */
export const acceptFriendRequest = async (
  friendshipId: number
): Promise<FriendshipView> => {
  const response = await apiFetch(
    `/api/v1/friends/requests/${friendshipId}/accept`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' }
    }
  );

  if (!response.ok) {
    throw new Error('Failed to accept friend request');
  }
  return response.json();
};

/**
 * Get list of friends
 */
export const getFriends = async (): Promise<
  components['schemas']['PageFriendshipView']
> => {
  const response = await apiFetch('/api/v1/friends', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch friends');
  }

  return response.json();
};

/**
 * Get the accepted connections the current user *received* — the people who
 * initiated a connection this user then accepted, i.e. the circles they joined
 * and the people they are watching over. Inbound counterpart to getFriends,
 * which only ever returns connections the user initiated; the two lists are
 * disjoint, so a combined circle is a plain merge with no de-duplication.
 *
 * Each item describes the initiator in `friendInfo` (`friendCustomerId` is the
 * current user, not someone to render).
 */
export const getReceivedConnections = async ({
  size
}: {
  size?: number;
} = {}): Promise<components['schemas']['PageFriendshipView']> => {
  const query = size === undefined ? '' : `?size=${size}`;
  const response = await apiFetch(`/api/v1/friends/received${query}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch received connections');
  }

  return response.json();
};

export const cancelFriendRequest = async (
  friendshipId: number
): Promise<void> => {
  const response = await apiFetch(`/api/v1/friends/${friendshipId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    throw new Error('Failed to cancel friend request');
  }
};

export const removeFriend = async (friendshipId: number): Promise<void> => {
  const response = await apiFetch(`/api/v1/friends/${friendshipId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' }
  });
  if (!response.ok) {
    throw new Error('Failed to remove friend');
  }
};

export const cancelInvitation = async (invitationId: number): Promise<void> => {
  const response = await apiFetch(
    `/api/v1/friends/invitations/${invitationId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }
  );
  if (!response.ok) {
    throw new Error('Failed to cancel invitation');
  }
};

/**
 * Send a friend invitation
 */
export const connectWithFriend = async (
  data: SendFriendConnectionViewType
): Promise<void> => {
  const response = await apiFetch('/api/v1/friends/connect', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.errorCode ||
        errorData.description ||
        'Failed to send friend invitation'
    );
  }
};
